import type { LDMultiKindContext, LDSingleKindContext } from '@launchdarkly/node-server-sdk';

type PrimitiveKeyValue = Record<string, string | number | boolean | symbol>;

type UserContextInput = {
  userId: string;
  email?: string;
} & PrimitiveKeyValue;

type OrganizationContextInput = {
  organizationId: string;
  organizationName: string;
} & PrimitiveKeyValue;

export const ContextType = Object.freeze({
  namespace: 'namespace',
  organization: 'organization',
  user: 'user',
});

export const createContextFactory = ({
  namespace,
  ...globalExtras
}: { namespace: string } & PrimitiveKeyValue) => {
  const namespaceContext = (extras: PrimitiveKeyValue) => ({
    key: namespace,
    ...globalExtras,
    ...extras,
  });
  const organizationContext = ({
    organizationId,
    organizationName,
    ...extras
  }: OrganizationContextInput & PrimitiveKeyValue) => ({
    key: organizationId,
    name: organizationName,
    ...globalExtras,
    ...extras,
  });

  const userContext = ({ userId, email, ...extras }: PrimitiveKeyValue & UserContextInput) => ({
    key: userId,
    email,
    ...globalExtras,
    ...extras,
  });

  return {
    organizationContext: (input: OrganizationContextInput): LDSingleKindContext => ({
      kind: ContextType.organization,
      ...organizationContext(input),
    }),
    userContext: (input: UserContextInput): LDSingleKindContext => ({
      kind: ContextType.user,
      ...userContext(input),
    }),
    multiContext: ({
      namespaceInput = {},
      userInput,
      organizationInput,
    }: {
      namespaceInput?: PrimitiveKeyValue;
      userInput?: UserContextInput;
      organizationInput?: OrganizationContextInput;
    } = {}): LDMultiKindContext => ({
      kind: 'multi',
      ...(userInput ? { user: userContext(userInput) } : {}),
      ...(organizationInput ? { organization: organizationContext(organizationInput) } : {}),
      namespace: namespaceContext(namespaceInput),
    }),
  };
};
